import React from 'react';
import styled from 'styled-components';
import { CheckCircleIcon, LightbulbIcon, UsersIcon, RocketIcon } from './IconComponents';
import { useTranslation, Trans } from 'react-i18next';

const AboutSection = styled.section`
  padding: 5rem 0;
  background-color: var(--background-color);
`;

const AboutContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const AboutContent = styled.div`
  @media (max-width: 992px) {
    order: 2;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  color: var(--text-color);

  span {
    color: var(--primary-color);
  }
`;

const AboutText = styled.p`
  margin-bottom: 2rem;
  line-height: 1.8;
  color: var(--text-color);
`;

const FeatureList = styled.ul`
  list-style: none;
  margin-bottom: 2rem;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;

  svg {
    color: var(--primary-color);
    margin-right: 1rem;
    font-size: 1.2rem;
  }
`;

const AboutImage = styled.div`
  position: relative;

  @media (max-width: 992px) {
    order: 1;
    height: 400px;
  }

  &::before {
    content: '';
    position: absolute;
    top: -20px;
    left: -20px;
    width: 100%;
    height: 100%;
    background-color: var(--primary-color);
    opacity: 0.1;
    border-radius: 8px;
    z-index: 1;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
    position: relative;
    z-index: 2;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }
`;

const ValuesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-top: 3rem;

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ValueCard = styled.div`
  background-color: var(--light-background);
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: var(--transition);

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
`;

const ValueIcon = styled.div`
  color: var(--primary-color);
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const ValueTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
  color: var(--text-color);
`;

const ValueDescription = styled.p`
  font-size: 0.9rem;
  color: var(--text-color);
`;

const About: React.FC = () => {
  const { t, i18n } = useTranslation();

  const values = t('about.values', { returnObjects: true }) as any[];

  return (
    <AboutSection id="about">
      <AboutContainer>
        <AboutContent>
          <SectionTitle>
            <Trans i18nKey="about.title">
              About <span>Byte Forge</span>
            </Trans>
          </SectionTitle>
          <AboutText>
            {t('about.paragraph1')}
          </AboutText>
          <AboutText>
            {t('about.paragraph2')}
          </AboutText>
          <AboutText>
            {t('about.paragraph3')}
          </AboutText>
          <FeatureList>
            {(t('about.features', { returnObjects: true }) as string[]).map((feature: string, index: number) => (
              <FeatureItem key={index}>
                <CheckCircleIcon /> {feature}
              </FeatureItem>
            ))}
          </FeatureList>

          <ValuesContainer>
            {values.map((value: any, index: number) => (
              <ValueCard key={index}>
                <ValueIcon>
                  {index === 0 && <LightbulbIcon />}
                  {index === 1 && <UsersIcon />}
                  {index === 2 && <CheckCircleIcon />}
                  {index === 3 && <RocketIcon />}
                </ValueIcon>
                <ValueTitle>{value.title}</ValueTitle>
                <ValueDescription>{value.description}</ValueDescription>
              </ValueCard>
            ))}
          </ValuesContainer>
        </AboutContent>

        <AboutImage>
          <img 
            src="/images/unsplash/about-team.jpg" 
            alt="Byte Forge team working together" 
          />
        </AboutImage>
      </AboutContainer>
    </AboutSection>
  );
};

export default About;
